import { AppleDevice, Device, DeviceState as DeviceStateEnum, Location, Room } from '@era/locations-sdk';
import { createFeatureSelector, createSelector } from '@ngrx/store';

import { AppleDeviceStoreSelectors } from '../apple-device-store';
import { CategoryStoreSelectors } from '../category';
import { LocationsRouterStoreSelectors } from '../router';

import * as fromReducer from './device.reducer';
import { DeviceState } from './device.state';

import { AssignedDevice } from './assigned-device.model';
import { SelectedLocation } from './selected-location.model';

export const selectDeviceState = createFeatureSelector<DeviceState>('devices');

export const selectDeviceIds = createSelector(selectDeviceState, fromReducer.selectDeviceIds);

export const selectLocationEntities = createSelector(selectDeviceState, fromReducer.selectDeviceEntities);

export const selectAllDevices = createSelector(selectDeviceState, fromReducer.selectAllDevices);

export const selectDevicesForLocation = (locationUniqueId: string) =>
  createSelector(selectAllDevices, (devices) =>
    devices ? devices.filter((device) => device.locationUniqueId === locationUniqueId) : []
  );

export const selectLocationDevicesByState = (locationUniqueId: string, state: DeviceStateEnum) =>
  createSelector(selectAllDevices, (devices: Device[]) =>
    devices ? devices.filter((device) => device.locationUniqueId === locationUniqueId && device.state === state) : []
  );

export const selectCurrentLocationDevices = createSelector(
  selectAllDevices,
  LocationsRouterStoreSelectors.selectLocation,
  (devices: Device[], location: Location) =>
    devices && location ? devices.filter((device) => device.locationUniqueId === location.uniqueId) : []
);

export const selectCurrentLocationDevicesByState = (state: DeviceStateEnum) =>
  createSelector(selectCurrentLocationDevices, (devices: Device[]) =>
    devices ? devices.filter((device) => device.state === state) : []
  );

export const selectCurrentLocationAssignedDevices = createSelector(
  selectCurrentLocationDevicesByState(DeviceStateEnum.Assigned),
  CategoryStoreSelectors.selectAllCategoryEntitiesByType('Room'),
  (devices: Device[], rooms: Room[]) =>
    devices && rooms
      ? devices
          .filter((device) => !!device.roomUniqueId)
          .map((device) => {
            const room = rooms.find((room) => room.uniqueId === device.roomUniqueId);
            return { room, ...device } as AssignedDevice;
          })
          .sort(
            (a, b) => a.room && b.room && a.room.displayName.localeCompare(b.room.displayName, 'en', { numeric: true })
          )
      : []
);

export const selectCurrentLocationDevicesCountByDeviceState = (state: DeviceStateEnum) =>
  createSelector(selectCurrentLocationDevicesByState(state), (devices: Device[]) =>
    devices ? devices.filter((device) => !device.housekeepingUniqueId).length : 0
  );

export const selectCurrentLocationSpareDevices = createSelector(
  selectCurrentLocationDevicesByState(DeviceStateEnum.Spare),
  (devices: Device[]) => (devices ? devices.filter((device) => device.isMDMLostModeEnabled === false) : [])
);

export const selectCurrentLocationSpareDevicesCount = createSelector(
  selectCurrentLocationSpareDevices,
  (devices: Device[]) => (devices ? devices.length : 0)
);

export const selectCurrentLocationHousekeepingDevices = createSelector(
  selectCurrentLocationDevicesByState(DeviceStateEnum.Assigned),
  (devices: Device[]) => (devices ? devices.filter((device) => !!device.housekeepingUniqueId) : [])
);

export const selectCurrentLocationHousekeepingDevicesCount = createSelector(
  selectCurrentLocationHousekeepingDevices,
  (devices: Device[]) => (devices ? devices.length : 0)
);

export const selectCurrentLocationStaffDevices = createSelector(
  selectCurrentLocationDevicesByState(DeviceStateEnum.Assigned),
  (devices: Device[]) => (devices ? devices.filter((device) => !!device.staffUniqueId) : [])
);

export const selectCurrentLocationStaffDevicesCount = createSelector(
  selectCurrentLocationStaffDevices,
  (devices: Device[]) => (devices ? devices.length : 0)
);

export const selectCurrentLocationFrontDeskDevices = createSelector(
  selectCurrentLocationDevicesByState(DeviceStateEnum.Assigned),
  (devices: Device[]) => (devices ? devices.filter((device) => !!device.frontDeskUniqueId) : [])
);

export const selectCurrentLocationFrontDeskDevicesCount = createSelector(
  selectCurrentLocationFrontDeskDevices,
  (devices: Device[]) => (devices ? devices.length : 0)
);

export const selectCurrentLocationBarDevices = createSelector(
  selectCurrentLocationDevicesByState(DeviceStateEnum.Assigned),
  (devices: Device[]) => (devices ? devices.filter((device) => !!device.barUniqueId) : [])
);

export const selectCurrentLocationKioskDevices = createSelector(
  selectCurrentLocationDevicesByState(DeviceStateEnum.Assigned),
  (devices: Device[]) => (devices ? devices.filter((device) => !!device.kioskUniqueId) : [])
);

export const selectCurrentLocationKioskDevicesCount = createSelector(
  selectCurrentLocationKioskDevices,
  (devices: Device[]) => (devices ? devices.length : 0)
);

export const selectCurrentLocationBarDevicesCount = createSelector(
  selectCurrentLocationBarDevices,
  (devices: Device[]) => (devices ? devices.length : 0)
);

export const selectCurrentLocationSwappedDevices = createSelector(
  selectCurrentLocationDevicesByState(DeviceStateEnum.Spare),
  (devices: Device[]) => (devices ? devices.filter((device) => device.isMDMLostModeEnabled === true) : [])
);

export const selectCurrentLocationSwappedDevicesCount = createSelector(
  selectCurrentLocationSwappedDevices,
  (devices: Device[]) => (devices ? devices.length : 0)
);

export const selectDevicesForCurrentRoom = createSelector(
  selectAllDevices,
  LocationsRouterStoreSelectors.selectRoomId,
  (devices: Device[], roomUniqueId: string) =>
    devices ? devices.filter((device) => device.roomUniqueId === roomUniqueId) : []
);

export const selectDevicesByState = (state: DeviceStateEnum) =>
  createSelector(selectAllDevices, (devices: Device[]) =>
    devices ? devices.filter((device) => device.state === state) : []
  );

export const selectDevicesCountByState = (state: DeviceStateEnum) =>
  createSelector(selectAllDevices, (devices: Device[]) =>
    devices ? devices.reduce((acc, curr) => (curr.state === state ? acc + 1 : acc), 0) : undefined
  );

export const selectDeviceByUdid = (udid: string) =>
  createSelector(selectAllDevices, (devices: Device[]) => {
    return devices.find((d) => d.deviceId === udid);
  });

export const selectDeviceByCurrentAppleDevice = createSelector(
  AppleDeviceStoreSelectors.selectCurrentAppleDevice,
  selectAllDevices,
  (appleDevice: AppleDevice, devices: Device[]) =>
    appleDevice ? devices.find((d) => d.deviceId === appleDevice.udid) : undefined
);

export const selectAllowedToUnassign = createSelector(selectDeviceByCurrentAppleDevice, deviceAllowedToUnassign);
export function deviceAllowedToUnassign(device: Device) {
  return !!device && device.state !== DeviceStateEnum.Unassigned && device.state !== DeviceStateEnum.Assigned;
}

export const selectAllowedToMarkUnavailable = createSelector(
  selectDeviceByCurrentAppleDevice,
  deviceAllowedToMarkUnavailable
);
export function deviceAllowedToMarkUnavailable(device: Device) {
  return !!device && device.state !== DeviceStateEnum.Unavailable;
}

export const selectAllowedToAssign = createSelector(selectDeviceByCurrentAppleDevice, deviceAllowedToAssign);
export function deviceAllowedToAssign(device: Device) {
  return !!device && device.state !== DeviceStateEnum.Assigned && device.state !== DeviceStateEnum.Unavailable;
}

export const selectAllowedToAssignToHousekeeping = createSelector(
  selectDeviceByCurrentAppleDevice,
  deviceAllowedToAssignToHousekeeping
);
export function deviceAllowedToAssignToHousekeeping(device: Device) {
  return !!device && device.state !== DeviceStateEnum.Assigned && !device.housekeepingUniqueId;
}

export const selectAllowedToAssignToStaff = createSelector(
  selectDeviceByCurrentAppleDevice,
  deviceAllowedToAssignToStaff
);
export function deviceAllowedToAssignToStaff(device: Device) {
  return !!device && device.state !== DeviceStateEnum.Assigned && !device.staffUniqueId;
}

export const selectAllowedToAssignToFrontDesk = createSelector(
  selectDeviceByCurrentAppleDevice,
  deviceAllowedToAssignToFrontDesk
);
export function deviceAllowedToAssignToFrontDesk(device: Device) {
  return !!device && device.state !== DeviceStateEnum.Assigned && !device.frontDeskUniqueId;
}

export const selectAllowedToAssignToBar = createSelector(selectDeviceByCurrentAppleDevice, deviceAllowedToAssignToBar);
export function deviceAllowedToAssignToBar(device: Device) {
  return !!device && device.state !== DeviceStateEnum.Assigned && !device.barUniqueId;
}

export const selectAllowedToAssignToKiosk = createSelector(
  selectDeviceByCurrentAppleDevice,
  deviceAllowedToAssignToKiosk
);
export function deviceAllowedToAssignToKiosk(device: Device) {
  return !!device && device.state !== DeviceStateEnum.Assigned && !device.kioskUniqueId;
}

export const selectAllowedToMarkAsSpare = createSelector(selectDeviceByCurrentAppleDevice, deviceAllowedToMarkAsSpare);
export function deviceAllowedToMarkAsSpare(device: Device) {
  return !!device && device.state !== DeviceStateEnum.Spare;
}

export const selectedLocationForCurrentDevice = createSelector(
  selectDeviceByCurrentAppleDevice,
  CategoryStoreSelectors.selectAllCategoryEntitiesByType('Location'),
  (device: Device, locations: Location[]) =>
    device && locations ? locations.find((location) => location.uniqueId === device.locationUniqueId) : undefined
);

export const selectRoomForCurrentDevice = createSelector(
  selectDeviceByCurrentAppleDevice,
  CategoryStoreSelectors.selectAllCategoryEntitiesByType('Room'),
  (device: Device, rooms: Room[]) =>
    device && rooms ? rooms.find((room) => room.uniqueId === device.roomUniqueId) : undefined
);

export const selectSelectedLocation = createSelector(
  LocationsRouterStoreSelectors.selectBrandHumanId,
  selectedLocationForCurrentDevice,
  selectRoomForCurrentDevice,
  (brandId, location, room) => {
    return brandId
      ? ({
          brandId,
          locationId: location ? location.id : undefined,
          roomId: room ? room.id : undefined,
        } as SelectedLocation)
      : undefined;
  }
);

export const selectRoomsByLocationWithoutAnyDevice = (locationId: string) =>
  createSelector(
    CategoryStoreSelectors.selectRoomsByLocationId(locationId),
    selectAllDevices,
    (rooms: Room[], devices: Device[]) => {
      return rooms.filter((room) => devices.findIndex((device) => device.roomUniqueId === room.uniqueId) === -1);
    }
  );
